import React, { useState, useEffect, useMemo } from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import LazyLoad from 'react-lazyload'
import Head from 'next/head'
import { getProductsInventory } from '@api/apiCalls'
import trackError from '@helpers/trackError'
import { isBrowser, getLink, getCurrencyCode } from '@helpers/env'
import { getValueFromObject } from '@helpers/utils'
import { updateVariants } from '@helpers/filters'
import CustomHead from '@components/Head'
import useSizingWidgetData from '@helpers/hooks/useSizingWidgetData'
import { PRODUCTION_PATH, STAGING_PATH } from '@consts/index'
import {
  Product,
  ProductByType,
  ProductMeta,
  ProductVariant
} from 'types/product'
import { EmptyObject } from 'types/helpers'
import {
  ContentfulDocument,
  Storytelling as StorytellingType
} from 'types/contentful/graphQlApi'
import { YotpoStarRatingProps } from '@components/product/yotpoRatings/YotpoStarRatings'
import { ProductsBreakdownProps } from '@components/ui/ProductsBreakdown'
import styles from '../styles.module.scss'
import YotpoReviews from '../yotpoRatings/YotpoReviews'
import ProductContext from '../context'
import ProductContainer from '../index'

// we should fix the duration about getting the products. maybe put a loading gif
const SizingWidget = dynamic(() => import('../SizingWidget'))
const ProductProductsBreakdown = dynamic(
  () => import('../ProductProductsBreakdown')
)
const StoryTelling = dynamic(() => import('../StoryTelling'))
const Loader = dynamic(() => import('../../Loading/PageLoading'))

const basePath =
  process.env.API_ENV === 'production' ? PRODUCTION_PATH : STAGING_PATH

function makeProductSchema(
  product: ProductListProduct,
  productUrl: string,
  meta: { description: string },
  ratings: YotpoStarRatingProps['starRatings']
) {
  return {
    '@context': 'http://schema.org',
    '@type': 'Product',
    description: meta.description,
    name: product.product_type,
    image: product.image?.src,
    brand: product.product_feed?.brand,
    sku: product.product_feed?.product_id,
    mpn: product.product_feed?.product_id,
    color: product.title,
    offers: {
      '@type': 'Offer',
      url: productUrl,
      priceCurrency: getCurrencyCode(),
      price: Number(product.price) || null,
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition'
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: ratings?.average_score,
      ratingCount: ratings?.total_review
    }
  }
}

type ProductDiscount = {
  title: string
  description: string
}

export type ProductListProduct = Product & {
  discount: ProductDiscount
  byline: string
  variants?: ProductVariant[]
}

export type ProductsList = (ProductByType & {
  products: ProductListProduct[]
})[]

export type ProductListProps = {
  productsList: ProductsList
  productsBreakdown: ProductsBreakdownProps | null
  meta: ProductMeta
  starRatings: YotpoStarRatingProps['starRatings'] | null
  careCopy: ContentfulDocument | null
  productTypesStorytelling: Record<string, StorytellingType[]> | EmptyObject
  productIds: number[]
  productTypes: string[]
}

const ProductList = ({
  productsBreakdown = null,
  productIds,
  productTypes,
  starRatings = null,
  careCopy,
  productsList,
  productTypesStorytelling
}: ProductListProps) => {
  const { query, asPath } = useRouter()
  const productHandle = Array.isArray(query.productHandle)
    ? query.productHandle[0]
    : query.productHandle

  const [variantExist, setVariantExist] = useState(false)
  const [products, setProducts] = useState(productsList)

  const productUrl = `${basePath}${asPath}`

  const flatProducts = products.flatMap(product => product.products)
  const selectedProduct =
    flatProducts.find(product => {
      return product.handle.toLowerCase() === productHandle
    }) || flatProducts[0]

  const selectedProductId = selectedProduct?.id

  const { sizingWidgetData, sizingWidgetCategory } = useSizingWidgetData({
    productOptions: selectedProduct?.options,
    productType: selectedProduct?.product_type,
    handle: productHandle || '',
    isPreview: query.preview === 'true'
  })

  const productContext = useMemo(
    () => ({
      discount: selectedProduct?.discount || {},
      careCopy,
      tabs: products,
      variantExist
    }),
    [selectedProduct?.discount, careCopy, products, variantExist]
  )

  useEffect(() => {
    const fetchVariants = async () => {
      try {
        const handleVariants = await getProductsInventory(
          productIds,
          productTypes
        )
        const productsWithVariants = products.map(productType => ({
          ...productType,
          products: updateVariants(productType.products, handleVariants)
        }))
        setProducts(productsWithVariants)
        setVariantExist(true)
      } catch (e) {
        const variantsError = new Error('Product fetchVariants failed')
        const error = e instanceof Error ? e : variantsError
        trackError(variantsError, { error })
      }
    }
    fetchVariants()
  }, [])

  const shouldShowProductReviews = Number(starRatings?.total_review) > 0
  const shouldShowProductsBreakdown =
    productsBreakdown && productsBreakdown?.categories.items.length > 0
  const shouldShowSizingWidget = !!sizingWidgetData

  if (query && !selectedProduct) {
    if (Object.keys(query).length === 0 && isBrowser()) {
      global.window.location.href = getLink('/pages/404')
    }
    return <Loader />
  }

  const metaSeo = {
    title:
      getValueFromObject(selectedProduct, 'meta.seo_title', undefined) ||
      `${selectedProduct?.title} - ${selectedProduct?.product_type}`,
    description:
      getValueFromObject(selectedProduct, 'meta.seo_description', undefined) ||
      selectedProduct?.byline,
    image: {
      url: selectedProduct?.image?.src,
      width: selectedProduct?.image?.width,
      height: selectedProduct?.image?.height
    }
  }
  const storytelling =
    productTypesStorytelling[selectedProduct?.product_type || '']

  return (
    <div className={styles.productContainer}>
      <Head>
        <script
          key={`jobJSON-${selectedProduct?.id}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              makeProductSchema(
                selectedProduct,
                productUrl,
                metaSeo,
                starRatings
              )
            )
          }}
        />
      </Head>
      <CustomHead
        title={metaSeo.title}
        desc={metaSeo.description}
        image={metaSeo.image}
        includeNoFollow={false}
      />
      <ProductContext.Provider value={productContext}>
        <ProductContainer
          selectedProduct={selectedProduct}
          byline={selectedProduct?.byline || ''}
          productHandle={productHandle || ''}
          productColor={selectedProduct?.title || ''}
          variantExist={variantExist}
          starRatings={starRatings}
        />
      </ProductContext.Provider>
      {storytelling && (
        <StoryTelling data={storytelling} handle={productHandle || ''} />
      )}
      {shouldShowSizingWidget && (
        <SizingWidget
          sizingWidgetData={sizingWidgetData}
          productType={sizingWidgetCategory || ''}
          productPage={true}
        />
      )}
      {shouldShowProductsBreakdown && (
        <LazyLoad once>
          <ProductProductsBreakdown
            productsBreakdown={productsBreakdown}
            productOptions={selectedProduct?.options || []}
            productType={selectedProduct?.product_type || ''}
          />
        </LazyLoad>
      )}
      {shouldShowProductReviews && (
        <div className={styles.yotpoRatings__Wrapper}>
          <YotpoReviews
            productId={String(selectedProduct?.id)}
            productTitle={selectedProduct?.title || ''}
            productImage={selectedProduct?.image?.src || ''}
            byline={selectedProduct?.byline}
          />
        </div>
      )}
    </div>
  )
}

export default ProductList
